import {
  Bell,
  CalendarDays,
  ChartColumn,
  CheckCheck,
  Gauge,
  KeyRound,
  LayoutDashboard,
  Lightbulb,
  Link2,
  LogOut,
  MessageSquare,
  Plus,
  Rocket,
  ScrollText,
  Search,
  Settings,
  Users,
  WandSparkles,
  Zap,
  Clock3,
  House,
  Building2,
  Images,
  Workflow,
  PanelLeftClose,
  PanelLeftOpen,
} from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router";
import { useThreads } from "@/api/chat-hooks";
import { useQueryClient } from "@tanstack/react-query";
import { clearChatSelection } from "../chat/runtime";
import { useTool, useToolMutation } from "@/api/hooks";
import { pickArray, type Alert, type DecisionsToday } from "@/api/types";
import { cn } from "@/lib/cn";
import { initials, relative } from "@/lib/format";
import { displayName, useAuth } from "@/state/auth";
import { brandPath, useBrandId } from "@/state/brand";
import { CountBadge } from "../ui/Badge";
import { LogoMark, Spinner } from "../ui/Feedback";
import { SearchInput } from "../ui/Form";
import { MenuItem, MenuLabel, MenuSeparator, PopoverPanel, Tooltip, usePopover } from "../ui/Overlay";

export function decisionsCount(d: DecisionsToday | undefined | null): number {
  if (!d) return 0;
  if (typeof d.total === "number") return d.total;
  return (
    (d.approvals?.length ?? 0) +
    (d.pendingActions?.length ?? 0) +
    (d.recommendations?.length ?? 0) +
    (d.manualUploads?.length ?? 0) +
    (d.publishedNotActive?.length ?? 0)
  );
}

const RAIL_KEY = "mimic.railExpanded";

/** Whether the rail shows labels. Collapsed (icons only) is the default; the choice is remembered per browser. */
export function readRailExpanded(): boolean {
  try {
    return localStorage.getItem(RAIL_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * Expanded/collapsed rail. Sets `data-rail` on <html>, which widens `--rail-w` (index.css) so the shell's content
 * padding follows. ⌘\ / Ctrl+\ toggles.
 */
export function useRailExpanded(): [boolean, () => void] {
  const [expanded, setExpanded] = useState(readRailExpanded);
  const toggle = useCallback(() => setExpanded((e) => !e), []);
  useLayoutEffect(() => {
    document.documentElement.dataset.rail = expanded ? "expanded" : "collapsed";
    try {
      localStorage.setItem(RAIL_KEY, expanded ? "1" : "0");
    } catch {
      /* private mode */
    }
  }, [expanded]);
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "\\") {
        e.preventDefault();
        toggle();
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [toggle]);
  return [expanded, toggle];
}

interface RailItemProps {
  to: string;
  icon: ReactNode;
  label: ReactNode;
  badge?: number;
  dot?: boolean;
  end?: boolean;
  matchPrefix?: string;
  expanded?: boolean;
}

function railItemClass(active: boolean, expanded = false) {
  return cn(
    "relative text-muted transition-colors hover:bg-hover hover:text-fg [&>svg]:shrink-0",
    expanded ? "flex h-9 w-full items-center gap-3 rounded-lg px-2.5 text-[13px] font-medium" : "grid size-10 place-items-center rounded-xl",
    active && "bg-elevated-2/80 text-fg shadow-[inset_0_0_0_1px_rgba(255,255,255,0.04)]",
  );
}

/** Icon plus, when expanded, its label; the badge/dot sits on the icon (collapsed) or trails the label (expanded). */
function ItemBody({ icon, label, badge, dot, expanded }: Pick<RailItemProps, "icon" | "label" | "badge" | "dot" | "expanded">) {
  if (!expanded) {
    return (
      <>
        {icon}
        {badge ? <CountBadge count={badge} className="absolute -right-0.5 -top-0.5" /> : null}
        {dot && <span className="absolute right-2 top-2 size-1.5 rounded-full bg-lavender ring-2 ring-rail" />}
      </>
    );
  }
  return (
    <>
      {icon}
      <span className="min-w-0 flex-1 truncate text-left">{label}</span>
      {badge ? <CountBadge count={badge} /> : null}
      {dot && <span className="size-1.5 shrink-0 rounded-full bg-lavender" />}
    </>
  );
}

/** Tooltip only when collapsed: expanded items show their label, and Tooltip's inline wrapper would shrink full-width rows. */
function RailTip({ label, expanded, disabled, children }: { label: ReactNode; expanded?: boolean; disabled?: boolean; children: ReactNode }) {
  if (expanded) return <>{children}</>;
  return (
    <Tooltip content={label} side="right" disabled={disabled}>
      {children}
    </Tooltip>
  );
}

function RailItem({ to, icon, label, badge, dot, matchPrefix, expanded }: RailItemProps) {
  const loc = useLocation();
  const active = matchPrefix ? loc.pathname.includes(matchPrefix) : undefined;
  return (
    <RailTip label={label} expanded={expanded}>
      <NavLink to={to} aria-label={typeof label === "string" ? label : undefined} className={({ isActive }) => railItemClass(active ?? isActive, expanded)}>
        <ItemBody icon={icon} label={label} badge={badge} dot={dot} expanded={expanded} />
      </NavLink>
    </RailTip>
  );
}

/** Rail icon with a hover flyout (Ad Monitor dashboards, Chats). */
function RailFlyout({ to, icon, label, matchPrefix, children, dot, panelClassName, expanded }: RailItemProps & { children: (close: () => void) => ReactNode; panelClassName?: string }) {
  const p = usePopover();
  const loc = useLocation();
  const timer = useRef<number | undefined>(undefined);
  const active = matchPrefix ? loc.pathname.includes(matchPrefix) : loc.pathname.startsWith(to);
  const openSoon = () => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => p.setOpen(true), 90);
  };
  const closeSoon = () => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => p.setOpen(false), 180);
  };
  return (
    <div onPointerEnter={openSoon} onPointerLeave={closeSoon}>
      <NavLink
        to={to}
        ref={(el) => {
          p.anchorRef.current = el;
        }}
        aria-label={typeof label === "string" ? label : undefined}
        className={railItemClass(active, expanded)}
        onClick={() => p.setOpen(false)}
      >
        <ItemBody icon={icon} label={label} dot={dot} expanded={expanded} />
      </NavLink>
      <PopoverPanel
        open={p.open}
        onClose={p.close}
        anchorRef={p.anchorRef}
        side="right"
        align="start"
        offset={10}
        className={cn("w-64 p-1.5", panelClassName)}
        onPointerEnter={() => window.clearTimeout(timer.current)}
        onPointerLeave={closeSoon}
      >
        <div className="px-2 pb-1 pt-1 text-[12px] font-semibold text-fg">{label}</div>
        {children(p.close)}
      </PopoverPanel>
    </div>
  );
}

function AdMonitorFlyout({ brandId, close }: { brandId: string | null; close: () => void }) {
  const navigate = useNavigate();
  const q = useTool<unknown>("dashboards.list", brandId ? { brandId } : null);
  const dashboards = pickArray<{ id: string; name?: string; title?: string }>(q.data, "dashboards");
  const base = brandPath(brandId, "monitor");
  return (
    <div className="flex flex-col">
      <MenuItem
        icon={<Zap className="size-4 text-amber" />}
        onClick={() => {
          navigate(base);
          close();
        }}
      >
        Live Overview
      </MenuItem>
      {q.isLoading ? (
        <div className="flex items-center gap-2 px-2.5 py-2 text-xs text-muted">
          <Spinner size={12} /> Loading dashboards…
        </div>
      ) : dashboards.length === 0 ? (
        <div className="px-2.5 py-2 text-xs text-subtle">{q.error ? "Dashboards unavailable" : "No saved dashboards yet"}</div>
      ) : (
        <div className="max-h-64 overflow-y-auto scrollbar-thin">
          {dashboards.map((d) => (
            <MenuItem
              key={d.id}
              icon={<LayoutDashboard className="size-4" />}
              onClick={() => {
                navigate(`${base}/${d.id}`);
                close();
              }}
            >
              {d.name ?? d.title ?? "Dashboard"}
            </MenuItem>
          ))}
        </div>
      )}
      <MenuSeparator />
      <MenuItem
        icon={<Plus className="size-4 text-link" />}
        className="text-link"
        onClick={() => {
          navigate(`${base}?new=1`);
          close();
        }}
      >
        New dashboard
      </MenuItem>
    </div>
  );
}

function ChatsFlyout({ close }: { close: () => void }) {
  const navigate = useNavigate();
  const brandId = useBrandId();
  const q = useThreads({ brandId });
  const [search, setSearch] = useState("");
  const threads = useMemo(() => {
    const all = q.data ?? [];
    const s = search.trim().toLowerCase();
    return s ? all.filter((t) => (t.title ?? "").toLowerCase().includes(s)) : all;
  }, [q.data, search]);
  return (
    <div className="flex flex-col gap-1">
      <div className="px-1 pb-1">
        <SearchInput value={search} onChange={setSearch} placeholder="Search chats..." className="[&_input]:h-8 [&_input]:text-[12.5px]" />
      </div>
      <MenuLabel>This brand’s chats</MenuLabel>
      <div className="max-h-72 overflow-y-auto scrollbar-thin">
        {q.isLoading ? (
          <div className="flex items-center gap-2 px-2.5 py-2 text-xs text-muted">
            <Spinner size={12} /> Loading…
          </div>
        ) : q.error ? (
          <div className="px-2.5 py-2 text-xs text-subtle">Chats unavailable</div>
        ) : threads.length === 0 ? (
          <div className="px-2.5 py-2 text-xs text-subtle">{search ? "No matching chats" : "No chats yet"}</div>
        ) : (
          threads.slice(0, 30).map((t) => (
            <MenuItem
              key={t.id}
              icon={t.scheduleId ? <Clock3 className="size-3.5" /> : undefined}
              right={t.unread ? <span className="size-1.5 rounded-full bg-lavender" /> : undefined}
              onClick={() => {
                navigate(`/chat/${t.id}`);
                close();
              }}
            >
              {t.title || "Untitled chat"}
            </MenuItem>
          ))
        )}
      </div>
      <MenuSeparator />
      <MenuItem
        icon={<Plus className="size-4 text-link" />}
        className="text-link"
        onClick={() => {
          navigate("/chat");
          close();
        }}
      >
        New chat
      </MenuItem>
    </div>
  );
}

function AlertsBell({ brandId, expanded }: { brandId: string | null; expanded?: boolean }) {
  const p = usePopover();
  const q = useTool<unknown>("alerts.list", { status: "open", ...(brandId ? { brandId } : {}) }, { refetchInterval: 60_000 });
  const alerts = pickArray<Alert>(q.data, "alerts").filter((a) => a.status !== "resolved");
  const ack = useToolMutation("alerts.ack", { silentError: false });
  const critical = alerts.some((a) => a.severity === "critical");
  return (
    <>
      <RailTip label="Alerts" expanded={expanded} disabled={p.open}>
        <button
          type="button"
          ref={(el) => {
            p.anchorRef.current = el;
          }}
          onClick={p.toggle}
          aria-label="Alerts"
          className={railItemClass(p.open, expanded)}
        >
          <Bell className="size-[18px]" strokeWidth={1.75} />
          {expanded && <span className="min-w-0 flex-1 truncate text-left">Alerts</span>}
          {alerts.length > 0 && (
            <span className={cn("size-2 rounded-full", expanded ? "shrink-0" : "absolute right-2 top-2 ring-2 ring-rail", critical ? "bg-danger" : "bg-amber")} />
          )}
        </button>
      </RailTip>
      <PopoverPanel open={p.open} onClose={p.close} anchorRef={p.anchorRef} side="right" align="end" offset={10} className="w-80 p-1.5">
        <div className="flex items-center justify-between px-2 pb-1 pt-1">
          <span className="text-[12px] font-semibold">Alerts</span>
          <Link to="/settings/alerts" onClick={p.close} className="text-[11.5px] text-link hover:text-lavender">
            View all
          </Link>
        </div>
        <div className="max-h-80 overflow-y-auto scrollbar-thin">
          {q.isLoading ? (
            <div className="px-2.5 py-3 text-xs text-muted">Loading…</div>
          ) : q.error ? (
            <div className="px-2.5 py-3 text-xs text-subtle">Alerts unavailable</div>
          ) : alerts.length === 0 ? (
            <div className="px-2.5 py-4 text-center text-xs text-subtle">All clear — no open alerts.</div>
          ) : (
            alerts.slice(0, 12).map((a) => (
              <div key={a.id} className="group flex items-start gap-2.5 rounded-lg px-2.5 py-2 hover:bg-hover">
                <span className={cn("mt-1.5 size-1.5 shrink-0 rounded-full", a.severity === "critical" ? "bg-danger" : a.severity === "warning" ? "bg-amber" : "bg-info")} />
                <div className="min-w-0 flex-1">
                  <div className="text-[12.5px] font-medium leading-snug">{a.title}</div>
                  {a.body && <div className="mt-0.5 line-clamp-2 text-[11.5px] text-muted">{a.body}</div>}
                  <div className="mt-1 flex items-center gap-2 text-[10.5px] text-subtle">
                    <span>{a.kind.replace(/_/g, " ")}</span>·<span>{relative(a.createdAt)}</span>
                    {a.status === "open" && (
                      <button type="button" className="ml-auto text-link opacity-0 group-hover:opacity-100" onClick={() => ack.mutate({ alertId: a.id, ...(a.brandId ? { brandId: a.brandId } : {}) })}>
                        Acknowledge
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </PopoverPanel>
    </>
  );
}

function UserMenu({ expanded }: { expanded?: boolean }) {
  const { me, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const p = usePopover();
  const go = (to: string) => {
    navigate(to);
    p.close();
  };
  const avatar = (
    <span className="grid size-8 shrink-0 place-items-center rounded-full bg-primary/25 text-[12px] font-semibold text-lavender ring-1 ring-primary/40 transition group-hover:bg-primary/35">
      {initials(me?.name || me?.email)}
    </span>
  );
  return (
    <>
      <button
        type="button"
        ref={(el) => {
          p.anchorRef.current = el;
        }}
        onClick={p.toggle}
        aria-label="Account menu"
        className={cn("group", expanded ? "flex w-full items-center gap-2.5 rounded-lg px-1 py-1 text-left transition-colors hover:bg-hover" : "grid place-items-center")}
      >
        {avatar}
        {expanded && (
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[12.5px] font-medium text-fg">{me?.name || displayName(me)}</span>
            <span className="block truncate text-[11px] text-muted">{me?.email ?? ""}</span>
          </span>
        )}
      </button>
      <PopoverPanel open={p.open} onClose={p.close} anchorRef={p.anchorRef} side="right" align="end" offset={12} className="w-60 p-1.5">
        <div className="px-2.5 pb-2 pt-1.5">
          <div className="truncate text-[13px] font-semibold">{me?.name || displayName(me)}</div>
          <div className="truncate text-[11.5px] text-muted">{me?.email ?? "Not signed in"}</div>
          {me && <div className="mt-1 text-[10.5px] uppercase tracking-wider text-subtle">{me.orgRole}</div>}
        </div>
        <MenuSeparator />
        <MenuItem icon={<Users className="size-4" />} onClick={() => go("/settings/team")}>
          Team
        </MenuItem>
        <MenuItem icon={<KeyRound className="size-4" />} onClick={() => go("/settings/tokens")}>
          API tokens
        </MenuItem>
        <MenuItem icon={<Settings className="size-4" />} onClick={() => go("/settings/general")}>
          Settings
        </MenuItem>
        {isAdmin && (
          <MenuItem icon={<ScrollText className="size-4" />} onClick={() => go("/settings/audit")}>
            Audit log
          </MenuItem>
        )}
        <MenuItem icon={<Building2 className="size-4" />} onClick={() => go("/brands")}>
          Brands
        </MenuItem>
        <MenuSeparator />
        <MenuItem icon={<LogOut className="size-4" />} onClick={() => void logout()}>
          Sign out
        </MenuItem>
      </PopoverPanel>
    </>
  );
}

export function Rail({ onSearch }: { onSearch: () => void }) {
  const brandId = useBrandId();
  const [expanded, toggleExpanded] = useRailExpanded();
  const decisions = useTool<DecisionsToday>("decisions.today", brandId ? { brandId } : null, { refetchInterval: 120_000, staleTime: 60_000 });
  const threads = useThreads({ brandId });
  const qc = useQueryClient();
  const previousBrand = useRef(brandId);
  useLayoutEffect(() => {
    if (previousBrand.current !== brandId) {
      previousBrand.current = brandId;
      clearChatSelection(qc);
    }
  }, [brandId, qc]);
  const unread = (threads.data ?? []).some((t) => t.unread);
  const ic = "size-[18px]";
  const sw = 1.75;
  const x = expanded;
  const align = expanded ? "items-stretch" : "items-center";
  const sep = <div className={cn("my-1.5 h-px bg-line", expanded ? "mx-2.5" : "mx-auto w-6")} />;
  const kbd = (k: string) => <kbd className="rounded border border-line px-1 font-sans text-[10.5px] text-subtle">{k}</kbd>;

  return (
    <nav
      aria-label="Primary"
      data-expanded={expanded || undefined}
      className={cn("fixed inset-y-0 left-0 z-40 hidden w-[var(--rail-w)] flex-col border-r border-line/60 bg-rail py-3 transition-[width] duration-200 md:flex", align, expanded && "px-2.5")}
    >
      <Link to={brandPath(brandId, "home")} aria-label="Mimic home" className={cn("group mb-3 flex items-center gap-2.5", expanded && "px-1")}>
        <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-white text-black transition group-hover:scale-105">
          <LogoMark className="size-5" />
        </span>
        {expanded && <span className="truncate text-[13.5px] font-semibold text-fg">Mimic Ads Studio</span>}
      </Link>
      <div className={cn("flex flex-1 flex-col gap-1 overflow-y-auto scrollbar-none", align)}>
        <RailTip label={<span>Search <span className="text-black/50">⌘K</span></span>} expanded={x}>
          <button type="button" onClick={onSearch} aria-label="Search" className={railItemClass(false, x)}>
            <Search className={ic} strokeWidth={sw} />
            {x && (
              <>
                <span className="min-w-0 flex-1 truncate text-left">Search</span>
                {kbd("⌘K")}
              </>
            )}
          </button>
        </RailTip>
        <RailTip label="New chat" expanded={x}>
          <NavLink to="/chat" end aria-label="New chat" className={({ isActive }) => cn(railItemClass(isActive, x), "border border-line-strong/70")}>
            <ItemBody icon={<Plus className={ic} strokeWidth={sw} />} label="New chat" expanded={x} />
          </NavLink>
        </RailTip>
        <RailItem expanded={x} to={brandPath(brandId, "home")} icon={<House className={ic} strokeWidth={sw} />} label="Home" badge={decisionsCount(decisions.data)} matchPrefix="/home" />
        <RailItem expanded={x} to={brandPath(brandId, "decisions")} icon={<CheckCheck className={ic} strokeWidth={sw} />} label="Decisions today" matchPrefix="/decisions" />
        <RailItem expanded={x} to={brandPath(brandId, "scheduler")} icon={<CalendarDays className={ic} strokeWidth={sw} />} label="Schedules" matchPrefix="/scheduler" />
        <RailItem expanded={x} to={brandPath(brandId, "connections")} icon={<Link2 className={ic} strokeWidth={sw} />} label="Connections" matchPrefix="/connections" />
        {sep}
        <RailItem expanded={x} to={brandPath(brandId, "inspiration")} icon={<Lightbulb className={ic} strokeWidth={sw} />} label="Inspiration" matchPrefix="/inspiration" />
        <RailItem expanded={x} to={brandPath(brandId, "studio")} icon={<WandSparkles className={ic} strokeWidth={sw} />} label="Studio" matchPrefix="/studio" />
        <RailItem expanded={x} to={brandPath(brandId, "flows")} icon={<Workflow className={ic} strokeWidth={sw} />} label="Flows" matchPrefix="/flows" />
        <RailItem expanded={x} to={brandPath(brandId, "creations")} icon={<Rocket className={ic} strokeWidth={sw} />} label="Creations" matchPrefix="/creations" />
        <RailItem expanded={x} to={brandPath(brandId, "assets")} icon={<Images className={ic} strokeWidth={sw} />} label="Brand assets" matchPrefix="/assets" />
        {sep}
        <RailFlyout expanded={x} to={brandPath(brandId, "monitor")} icon={<ChartColumn className={ic} strokeWidth={sw} />} label="Ad Monitor" matchPrefix="/monitor">
          {(close) => <AdMonitorFlyout brandId={brandId} close={close} />}
        </RailFlyout>
        <RailItem expanded={x} to={brandPath(brandId, "performance")} icon={<Gauge className={ic} strokeWidth={sw} />} label="Creative Performance" matchPrefix="/performance" />
        {sep}
        <RailFlyout expanded={x} to="/chat" icon={<MessageSquare className={ic} strokeWidth={sw} />} label={unread ? "Chats · Unread reply" : "Chats"} matchPrefix="/chat/" dot={unread} panelClassName="w-72">
          {(close) => <ChatsFlyout key={brandId} close={close} />}
        </RailFlyout>
      </div>
      <div className={cn("mt-2 flex flex-col gap-2 border-t border-line/60 pt-3", align)}>
        <RailTip label={<span>Expand sidebar <span className="text-black/50">⌘\</span></span>} expanded={x}>
          <button type="button" onClick={toggleExpanded} aria-label={x ? "Collapse sidebar" : "Expand sidebar"} aria-expanded={x} className={railItemClass(false, x)}>
            {x ? <PanelLeftClose className={ic} strokeWidth={sw} /> : <PanelLeftOpen className={ic} strokeWidth={sw} />}
            {x && (
              <>
                <span className="min-w-0 flex-1 truncate text-left">Collapse</span>
                {kbd("⌘\\")}
              </>
            )}
          </button>
        </RailTip>
        <AlertsBell brandId={brandId} expanded={x} />
        <UserMenu expanded={x} />
      </div>
    </nav>
  );
}

/** Bottom tab bar for small screens (rail is hidden < md). */
export function MobileNav({ onSearch }: { onSearch: () => void }) {
  const brandId = useBrandId();
  const items = [
    { to: brandPath(brandId, "home"), icon: House, label: "Home" },
    { to: brandPath(brandId, "decisions"), icon: CheckCheck, label: "Decide" },
    { to: brandPath(brandId, "monitor"), icon: ChartColumn, label: "Monitor" },
    { to: brandPath(brandId, "creations"), icon: Rocket, label: "Creations" },
    { to: "/chat", icon: MessageSquare, label: "Chat" },
  ];
  return (
    <nav aria-label="Primary" className="fixed inset-x-0 bottom-0 z-40 flex items-stretch justify-around border-t border-line bg-rail/95 px-1 pb-[env(safe-area-inset-bottom)] backdrop-blur md:hidden">
      {items.map((it) => (
        <NavLink key={it.label} to={it.to} className={({ isActive }) => cn("flex flex-1 flex-col items-center gap-0.5 py-2 text-[10.5px]", isActive ? "text-fg" : "text-muted")}>
          <it.icon className="size-5" strokeWidth={1.75} />
          {it.label}
        </NavLink>
      ))}
      <button type="button" onClick={onSearch} className="flex flex-1 flex-col items-center gap-0.5 py-2 text-[10.5px] text-muted">
        <Search className="size-5" strokeWidth={1.75} />
        Search
      </button>
    </nav>
  );
}
